<template>
  <div class="register-password-form">
    <div class="heading">
        <h3 class="mb-s">{{ $t('title.confirmPasswordEmail') }}</h3>
        <div class="line-height-s">
          <i18n
            path="title.emailPasswordConfirmSubtitle"
            tag="span"
          >
            <strong slot="time">{{ $t("title.fromNow", { time: "48h"} ) }}</strong>
          </i18n>
        </div>
    </div>
    <p class="username-show">
      <span class="text-bold">{{ email }} </span>
      <bf-link underline=""><a
        class="text-decoration"
        href="#changeAccount"
        @click.prevent="changeAccount"
      >{{ $t("common.changeAccount") }}</a></bf-link>
    </p>
    <bf-form
      :ref="formName"
      class="mt-xs"
      :model="form"
      :rules="formRules"
      @submit.native.prevent="onSubmit">
      <bf-form-item prop="emailPassword">
        <label
          for="emailPassword"
          class="el-form-item__label"
        >{{ $t("form.emailPassword") }}</label>
        <bf-input
          :disabled="isLoading"
          ref="password"
          showPassword
          v-model="form.emailPassword"
          class="w-100"
          :placeholder="$t('form.placeholderEmailPassword')"
          :clearable="true"
        >
          <template slot="append">
            <bf-button
              :loading="isLoading"
              type="primary"
              @click.native.stop.prevent="onSubmit">
              <bf-icon name="arrow-forward" />
            </bf-button>
          </template>
        </bf-input>
      </bf-form-item>
    </bf-form>
    <!--begin::Gap-->
    <div class="gap pb-l" />
    <!--end::Gap-->
    <p :data-retry="retryTime">
      {{ $t('common.notreceivedAPasswordEmail')}}
      <span v-if="resendTime > 0">
        <span class="text-gray">
          {{ $t("common.resendPasswordAfter", { "second": resendTime } ) }}
        </span>
      </span>
      <bf-link v-else>
        <a href="#" @click.prevent="resendPassword">{{ $t("common.resendPassword") }}</a>
      </bf-link>
      </p>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { confirmPassword } from '@/services/register';
import { resendPasswordEmail } from '@/services/verification';
import { setToken } from '@/utils/common';
import RULES from '@/config/rules';

export default {
  name: 'ConfirmPasswordEmail',
  props: {
    email: {
      type: String,
      default: '',
    },
    count: {
      type: Number,
      default: 0,
    },
    retryTime: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isLoading: false,
      countdownId: null,
      resendTime: 5,
      formName: 'ConfirmPasswordEmail',
      form: {
        emailPassword: '',
      },
    };
  },
  computed: {
    ...mapGetters('device', [
      'isMobile',
    ]),
    ...mapGetters('auth', [
      'callbackUrl',
    ]),
    formRules() {
      return {
        emailPassword: [
          {
            required: true,
            message: this.$t('form.pleaseInput',
            { name: this.$t('form.emailPassword').toLowerCase() }),
            trigger: ['change'],
          },
          {
            pattern: RULES.PATTERN_PASSWORD,
            message: this.$t('form.validator.password', { field: this.$t('title.password'), min: '8', max: '255' }),
            trigger: ['change'],
          },
        ],
      };
    },
  },
  watch: {
    count(nextValue, oldValue) {
      if (nextValue !== oldValue) {
        this.countdownTimer(this.retryTime);
        this.onReset();
        this.focusInput();
      }
    },
  },
  mounted() {
    this.countdownTimer(0);
    this.focusInput();
  },
  destroyed() {
    clearInterval(this.countdownId);
  },
  methods: {
    ...mapActions('auth', ['updateTicket']),
    async onSubmit() {
      this.$refs[this.formName].validate(async (valid) => {
        if (valid) {
          this.isLoading = true;
          this.confirmPasswordEmail();
        }
      });
    },
    async confirmPasswordEmail() {
      const payload = {
        password: this?.form?.emailPassword,
        service: this.callbackUrl || '',
      };
      try {
        const response = await confirmPassword(payload);
        if (response?.data?.access_token) {
          setToken(response.data.access_token);
        }
        // Set ticket
        if (response?.data?.ticket) {
          this.updateTicket(response?.data?.ticket);
        }

        this.$notify({
          title: this.$t('notification.success.title'),
          message: response?.message,
          type: 'success',
          customClass: 'success',
        });
        this.isLoading = false;
        // Go next step
        this.$emit('updatePassword', this.form.emailPassword);
        this.$emit('goToStep', 'change-password-step');
      } finally {
        this.isLoading = false;
        this.focusInput();
      }
    },
    changeAccount() {
      this.$emit('goToStep', 'step-1');
    },
    countdownTimer(timer = 0) {
      clearInterval(this.countdownId);
      this.resendTime = timer;
      this.countdownId = setInterval(() => {
        if (this.resendTime > 0) {
          this.resendTime -= 1;
        } else {
          clearInterval(this.countdownId);
        }
      }, 1000);
    },
    async resendPassword() {
      this.isLoading = true;
      let retryAfter = 0;
      try {
        const payload = { send_type: 'PASSWORD' };
        const response = await resendPasswordEmail(payload);
        // Get retry time from api
        if (response?.retry_after) {
          retryAfter = response?.retry_after;
        }
        this.$notify({
          title: this.$t('notification.success.title'),
          message: response?.message,
          type: 'success',
          customClass: 'success',
        });
        this.countdownTimer(retryAfter);
        this.onReset();
        this.focusInput();
        this.isLoading = false;
      } catch (error) {
        if (error?.response?.data?.error === 'ResendVerificationPasswordTooFast') {
          retryAfter = error?.response?.data?.detail?.match(/\d+/g);
          retryAfter = retryAfter ?? retryAfter?.[0];
          this.countdownTimer(retryAfter);
        }
        this.focusInput();
      } finally {
        this.isLoading = false;
      }
    },
    focusInput() {
      this.$nextTick(() => {
        this.$refs?.password?.$el?.querySelector('input').focus();
      });
    },
    onReset() {
      this.$refs[this.formName].resetFields();
    },
  },
};
</script>
